/**
 * The `stay=app_portal` opt-out marker (CORE-6841).
 *
 * The Team Portal's "Back to App Portal" link lands here with `?stay=app_portal`. That marker is
 * forwarded to the Identity Server as an authorize query parameter so it knows this sign-in was a
 * deliberate return to the App Portal, and does not redirect an opted-in team's user straight back
 * to the Team Portal.
 *
 * Two rules make this work, and both are load-bearing:
 *
 * 1. The marker is read from the URL **once, at module load**. The Team Portal lands on `/`, whose
 *    route is a plain string `redirect` to `/dashboard`; vue-router 3 drops the query string across
 *    a string redirect, so by the time the auth guard calls `AuthService.login()` the marker may
 *    already be gone from `window.location.search`. Module evaluation happens during app bootstrap,
 *    before the router resolves its first route.
 * 2. The marker must be stripped from the OIDC `state`. `Callback.vue` compares the restored state
 *    against `'/'` to decide whether a new user is routed into onboarding — a state of
 *    `/?stay=app_portal` would silently skip onboarding for exactly the users arriving from the Team
 *    Portal. Stripping it also keeps the marker out of the address bar, which is what makes it
 *    one-shot.
 */

export const STAY_PARAM = 'stay';
export const STAY_APP_PORTAL = 'app_portal';

/**
 * Returns {@link STAY_APP_PORTAL} when the given query string carries `stay=app_portal`
 * (case-insensitive), otherwise `null`. Accepts a leading `?`, and tolerates null/undefined/''.
 */
export function readStayParam(search) {
    if (!search) {
        return null;
    }

    const value = new URLSearchParams(search).get(STAY_PARAM);

    return value && /^app_portal$/i.test(value) ? STAY_APP_PORTAL : null;
}

/**
 * Removes the `stay` key from a `path + search` string, dropping the `?` when nothing else remains.
 * Any other query parameters are preserved.
 */
export function stripStayParam(urlWithSearch) {
    if (!urlWithSearch) {
        return urlWithSearch;
    }

    const queryIndex = urlWithSearch.indexOf('?');
    if (queryIndex === -1) {
        return urlWithSearch;
    }

    const path = urlWithSearch.slice(0, queryIndex);
    const params = new URLSearchParams(urlWithSearch.slice(queryIndex + 1));
    params.delete(STAY_PARAM);

    const remaining = params.toString();

    return remaining ? `${path}?${remaining}` : path;
}

/**
 * `signinRedirect` arguments carrying the marker, or `{}` when it is absent — mirroring how
 * `ssoAcrArgs()` returns `{}` for a non-SSO browser, so `Object.assign` leaves the other args alone.
 */
export function stayArgs(search) {
    return readStayParam(search) ? { extraQueryParams: { [STAY_PARAM]: STAY_APP_PORTAL } } : {};
}

// Captured once, at module load — see rule 1 above.
const INITIAL_SEARCH = typeof window !== 'undefined' && window.location ? window.location.search : '';

/**
 * {@link stayArgs} for the URL this tab was opened with, regardless of any later router rewrite.
 */
export function initialStayArgs() {
    return stayArgs(INITIAL_SEARCH);
}
